import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudUpload } from 'lucide-react';
import { api } from '@/lib/api';
import { FolderPicker } from './FolderPicker';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime, formatNumber } from '@/lib/format';

/**
 * "Simpan ke Cloud" - mengubah hasil crawl menjadi berkas di Penyimpanan Pribadi.
 *
 * Sengaja menjadi tindakan yang dipilih pengguna, bukan otomatis setiap crawl
 * selesai. Sebagian besar crawl awal dijalankan untuk mencoba-coba selector,
 * dan menyimpan semuanya hanya menghabiskan kuota dengan berkas yang tak
 * diinginkan.
 */
export const SaveToCloudDialog = ({ open, onClose, job }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [filename, setFilename] = useState('');
  const [format, setFormat] = useState('csv');
  const [runId, setRunId] = useState('');
  const [folderId, setFolderId] = useState('');

  // Nama berkas diusulkan dari nama job plus tanggal, supaya pengguna cukup
  // menekan simpan tanpa harus memikirkan penamaan.
  useEffect(() => {
    if (!open || !job) return;
    const stamp = new Date().toISOString().slice(0, 10);
    setFilename(`${job.name} ${stamp}`);
    setFormat('csv');
    setRunId('');
    setFolderId('');
  }, [open, job]);

  const { data: runsData } = useQuery({
    queryKey: ['savable-runs', job?.id],
    queryFn: () => api.get(`/jobs/${job.id}/savable-runs`),
    enabled: open && Boolean(job?.id),
  });

  const runs = runsData?.data ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/storage/save', {
        jobId: job.id,
        runId: runId || undefined,
        filename,
        format,
        folderId: folderId || null,
      }),
    onSuccess: (response) => {
      toast.success('Tersimpan ke Penyimpanan Pribadi', {
        description: `${formatNumber(response.data.rowCount)} baris disimpan sebagai ${response.data.filename}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['storage'] });
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Gagal menyimpan', { description: error.message }),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Simpan ke Cloud"
      description="Hasil crawl akan disimpan sebagai berkas di Penyimpanan Pribadi Anda."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Batal
          </Button>
          <Button
            icon={CloudUpload}
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!filename.trim()}
          >
            Simpan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Nama Berkas"
          required
          hint="Karakter yang tidak aman akan dirapikan otomatis."
        >
          {(props) => (
            <Input
              {...props}
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="Misal: katalog produk agustus"
              data-autofocus
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Format">
            {(props) => (
              <Select
                {...props}
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                options={[
                  { value: 'csv', label: 'CSV - untuk Excel' },
                  { value: 'json', label: 'JSON - untuk program' },
                  { value: 'ndjson', label: 'NDJSON - data besar' },
                ]}
              />
            )}
          </Field>

          <Field
            label="Sumber Data"
            hint={runs.length ? undefined : 'Belum ada run yang menghasilkan data.'}
          >
            {(props) => (
              <Select
                {...props}
                value={runId}
                onChange={(e) => setRunId(e.target.value)}
                options={[
                  { value: '', label: 'Semua data job ini' },
                  ...runs.map((run) => ({
                    value: run.id,
                    label: `${formatDateTime(run.createdAt)} - ${formatNumber(
                      run.progress?.recordsExtracted ?? 0,
                    )} baris`,
                  })),
                ]}
              />
            )}
          </Field>
        </div>
        <Field label="Simpan ke Folder" hint="Kosongkan untuk akar penyimpanan.">
          {(props) => <FolderPicker {...props} value={folderId} onChange={setFolderId} />}
        </Field>


        <p className="rounded-lg bg-[var(--surface-sunken)] px-3.5 py-3 text-xs leading-relaxed text-[var(--text-muted)]">
          Setelah tersimpan, berkas bisa diunduh kapan saja, atau dipublikasikan ke Data Publik
          agar bisa dipakai orang lain.
        </p>
      </div>
    </Modal>
  );
};
